import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Crown, Star, Bell } from 'lucide-react';
import { motion } from 'framer-motion';

export function SubscriptionService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 px-4 pt-20 pb-8 md:p-8 relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-violet-200/30 to-pink-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-cyan-200/30 to-blue-200/30 rounded-full blur-3xl"></div>

      <div className="max-w-2xl w-full mx-auto relative z-10">

        {/* Back to Portal Button — always visible below fixed navbar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-5"
        >
          <button
            onClick={() => navigate('/learn/customer-work')}
            className="flex items-center gap-2 text-violet-700 hover:text-violet-900 hover:bg-violet-100 font-semibold px-4 py-2 rounded-xl border border-violet-200 bg-white/70 shadow-sm transition-all duration-200 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portal
          </button>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 rounded-3xl shadow-2xl p-6 md:p-10 border-2 border-white/80 backdrop-blur-sm"
        >
          {/* Animated Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="flex justify-center mb-6"
          >
            <div className="relative">
              <motion.div
                animate={{
                  rotate: [12, 15, 12],
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="w-20 h-20 bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-violet-500/50"
              >
                <Crown className="w-10 h-10 text-white" />
              </motion.div>
              {/* Notification badge */}
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full border-4 border-white shadow-xl flex items-center justify-center"
              >
                <span className="text-white text-sm font-black">!</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl md:text-4xl font-black text-center mb-4 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight"
          >
            Subscription Service Coming Soon!
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center text-gray-600 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed"
          >
            Our premium subscription plans with priority support and unlimited revisions are on their way. Stay tuned to the AGO website for updates!
          </motion.p>

          {/* Launching Soon Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="flex justify-center mb-8"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 text-white rounded-full font-bold text-lg shadow-2xl shadow-violet-500/50 hover:shadow-violet-500/70 transition-all duration-300 flex items-center gap-3 border-2 border-white/20"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="w-5 h-5" />
              </motion.div>
              Launching Soon
            </motion.button>
          </motion.div>

          {/* Feature Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-3 gap-2 sm:gap-4"
          >
            {[
              {
                icon: <Crown className="w-5 h-5 sm:w-7 sm:h-7 text-white" />,
                iconSm: <Crown className="w-5 h-5 text-white" />,
                bg: 'from-violet-500 to-purple-600',
                label: 'Premium Plans',
              },
              {
                icon: <Star className="w-5 h-5 sm:w-7 sm:h-7 text-white" />,
                bg: 'from-yellow-400 to-orange-500',
                label: 'Priority Support',
              },
              {
                icon: <Bell className="w-5 h-5 sm:w-7 sm:h-7 text-white" />,
                bg: 'from-pink-400 to-rose-500',
                label: 'Early Access',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.02 }}
                className="bg-white/70 backdrop-blur-sm rounded-2xl p-3 sm:p-5 text-center border-2 border-white shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br ${item.bg} rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-lg flex-shrink-0`}
                >
                  {item.icon}
                </motion.div>
                <h3 className="text-gray-800 font-bold text-xs sm:text-sm leading-tight text-center break-words w-full">
                  {item.label.split(' ').map((word, wi) => (
                    <span key={wi}>
                      {word}
                      {wi < item.label.split(' ').length - 1 && <br className="sm:hidden" />}
                    </span>
                  ))}
                </h3>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
